// ---------------------------------------------------------------------------
// product-configuration.ts — which product this browser session is running.
//
// Razor injects one product global, __gameplan_product_name, from the
// compile-time C# constant; everything else comes from the shared configuration
// table. Do not add more __gameplan_product_* globals — add members to
// product-configurations.ts instead.
//
// Mirrors GamePlanNodeJsFunctions/src/helpers/product-configuration.ts, which
// does the same job over that host's GAMEPLAN_PRODUCT_NAME.
// ---------------------------------------------------------------------------

import {
    PRODUCT_CONFIGURATIONS, DEFAULT_PRODUCT, toProductKey, withLocalPrefix,
    type IGamePlanProductConfiguration,
} from './product-configurations';

export type { IGamePlanProductConfiguration };
export { productWebsiteHost } from './product-configurations';

let cached: IGamePlanProductConfiguration | null = null;

export function gameplanProductConfiguration(): IGamePlanProductConfiguration {
    if (cached) return cached;
    const injected = typeof __gameplan_product_name === 'string' ? __gameplan_product_name : null;
    const config = PRODUCT_CONFIGURATIONS[toProductKey(injected) ?? DEFAULT_PRODUCT];
    // Both globals must be typeof-guarded, not merely read: Razor emits
    // __gameplan_is_local_development only on a local server, so on a deployed
    // environment the identifier is never declared and a bare read throws
    // ReferenceError — `declare var` in global.d.ts is type-only and emits
    // nothing. Same guard every other consumer of this flag uses.
    const isLocalDevelopment =
        typeof (__gameplan_is_local_development) !== 'undefined' && __gameplan_is_local_development;
    cached = isLocalDevelopment ? withLocalPrefix(config) : config;
    return cached;
}

/** Test seam. The cache is page-lifetime in production. */
export function resetProductConfigurationCacheForTests(): void {
    cached = null;
}
